import { Injector } from '@angular/core';
import { MutationOptions, QueryOptions } from '@apollo/client/core';
import { PrizmDayRange } from '@prizm-ui/components';
import { TuiDayRange } from '@taiga-ui/cdk';
import { format } from 'date-fns';
import { GqlFields, InputControlGqlValue } from 'ngx-register-base';
import { EDatePattern } from '../constants/date-time.constants';

type RangeLike = {
  from?: { toLocalNativeDate: () => Date } | Date;
  to?: { toLocalNativeDate: () => Date } | Date;
} | null;

export const getGQLValueEq = (value: string | null, field: string): GqlFields | undefined =>
  value ? { [field]: { _eq: value } } : undefined;

export const getGQLValueMultiSelect = (
  values: { id: string }[] | null,
  field: string
): GqlFields | undefined =>
  values?.length ? { [field]: { _in: values.map((v) => v.id) } } : undefined;

export const getGqlNameLike = (value: string | null, field: string): GqlFields | undefined =>
  value ? { [field]: { name: { _ilike: `%${value}%` } } } : undefined;

export const getGqlSubPropertyLike = (
  value: string | null,
  field: string,
  subfield: string
): GqlFields | undefined =>
  value ? { [field]: { [subfield]: { _ilike: `%${value}%` } } } : undefined;

export const getGQLIlike = (value: string | null, field: string): GqlFields | undefined =>
  value ? { [field]: { _ilike: `%${value}%` } } : undefined;

export const getGQLGte = (value: string | null, field: string): GqlFields | undefined =>
  value ? { [field]: { _gte: `%${value}%` } } : undefined;

export const getGQLLte = (value: string | null, field: string): GqlFields | undefined =>
  value ? { [field]: { _lte: `%${value}%` } } : undefined;

export const getGQLIsDeletedValue = (isDeleted: boolean | null): GqlFields | undefined =>
  typeof isDeleted === 'boolean' ? { is_deleted: { _eq: isDeleted } } : undefined;

export const getGQLDateTimeRangeValue = (
  range: RangeLike,
  field: string,
  toField?: string,
  toIso: (d: Date) => string = (d) => d.toISOString()
): GqlFields | undefined => {
  let result: GqlFields | undefined;

  if (range) {
    const fromRaw = (range as any).from;
    const toRaw = (range as any).to;
    const from = fromRaw instanceof Date ? fromRaw : fromRaw?.toLocalNativeDate?.();
    const to = toRaw instanceof Date ? toRaw : toRaw?.toLocalNativeDate?.();

    if (!(from && to)) {
      return undefined;
    }

    result = toField
      ? ({ [field]: { _gte: toIso(from) }, [toField]: { _lte: toIso(to) } } as unknown as GqlFields)
      : ({ [field]: { _gte: toIso(from), _lte: toIso(to) } } as unknown as GqlFields);
  }

  return result;
};

export const getGQLDayRangeValue = (
  value: PrizmDayRange | TuiDayRange | null,
  searchKey: string
): GqlFields | undefined => {
  if (!value) {
    return;
  }
  const setDateGreater = (date: Date) => {
    date.setDate(date.getDate() + 1);
    return date;
  };
  const dateTo = setDateGreater(value.to.toLocalNativeDate());
  const from = format(value.from.toLocalNativeDate(), EDatePattern.YEAR_MONTH_DAY);
  const to = format(dateTo, EDatePattern.YEAR_MONTH_DAY);

  return {
    [searchKey]: {
      _gte: from,
      _lt: to,
    },
  };
};

export type FormatterGqlValueType<ValueType> = (
  value: ValueType,
  injector?: Injector
) => InputControlGqlValue;

/**
 * Создает объект с GraphQL форматерами для стандартных полей фильтра
 *
 * @param fields Массив имен полей
 * @returns Объект с форматерами вида { fieldName: (value) => getGQLIlike(value, fieldName) }
 */
export const createStandardReferenceFilters = (
  fields: string[]
): Record<string, (value: string | null) => GqlFields | undefined> => {
  const filters: Record<string, (value: string | null) => GqlFields | undefined> = {};

  for (const field of fields) {
    filters[field] = (value: string | null) => getGQLIlike(value, field);
  }

  return filters;
};

/**
 * Преобразует массив записей к целевому типу без изменений данных.
 */
export const createSimpleConverter = <T extends Record<string, any>>(
  objects: Record<string, any>[]
): T[] => objects as T[];

interface CustomMutationOptions extends Omit<MutationOptions, 'fetchPolicy'> {
  fetchPolicy?: 'no-cache';
}

export interface CustomQueryOptions extends Omit<QueryOptions, 'fetchPolicy'> {
  fetchPolicy?: 'no-cache';
}

type FetchPolicyOptions =
  | Omit<CustomMutationOptions, 'fetchPolicy'>
  | Omit<CustomQueryOptions, 'fetchPolicy'>;

function noCache(options: FetchPolicyOptions): CustomMutationOptions | CustomQueryOptions {
  return {
    ...options,
    fetchPolicy: 'no-cache',
  };
}

export function noCacheQuery(options: Omit<CustomQueryOptions, 'fetchPolicy'>): CustomQueryOptions {
  return noCache(options) as CustomQueryOptions;
}

// Найти id в любом респонсе
export function findId(response: any): string | null {
  let result = null;

  if (response && response.id) {
    result = response.id;
  }

  if (Array.isArray(response)) {
    for (const item of response) {
      const arrId = findId(item);

      if (arrId) {
        result = arrId;
        break;
      }
    }
  }

  if (typeof response === 'object' && response !== null) {
    for (const key in response) {
      if (Object.prototype.hasOwnProperty.call(response, key)) {
        const objId = findId(response[key]);

        if (objId) {
          result = objId;
          break;
        }
      }
    }
  }

  return result;
}
